/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { FC, useState } from "react";
import { DropzoneDialog } from "mui-file-dropzone";
import Button from "./Button";
import { CoreAPI_URLs } from "constants/http.const";
import {
  AssetInterface,
  HeaderEnum,
  MediaCategoryEnum,
  MediaMimeTypesEnum,
  MediaSourceEnum,
  MediaStatusEnum,
} from "@malmur-technologies/medicalsystem-shared";
import useHttp from "hooks/http.hook";
import PermissionWrapper from "./PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import { useTranslation } from "react-i18next";

interface DropzoneProps {
  openedCategory?: string;
  patientId?: string;
  providerId?: string;
  paymentId?: string;
  setIsDataUpdated: (updated: boolean) => void;
}

const Dropzone: FC<DropzoneProps> = ({
  openedCategory,
  patientId,
  paymentId,
  providerId,
  setIsDataUpdated,
}) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const handleDialogChange = (loadedFiles: File[]) => {
    setOpen(false);

    if (!loadedFiles || loadedFiles.length === 0) {
      return;
    }

    let defaultCategory: boolean = false;
    if (
      openedCategory !== MediaCategoryEnum.CLINIC_DOCUMENT &&
      openedCategory !== MediaCategoryEnum.OTHER &&
      openedCategory !== MediaCategoryEnum.TEST_RESULTS
    ) {
      defaultCategory = true;
    }

    const data = {
      mimetype: loadedFiles[0].type,
      source: MediaSourceEnum.OTHER,
      category: defaultCategory ? MediaCategoryEnum.OTHER : openedCategory,
      orgName: loadedFiles[0].name,
      status: MediaStatusEnum.PENDING,
    };

    sendRequest<AssetInterface>(
      CoreAPI_URLs.ASSETS,
      (response) => {
        if (response) {
          const formData = new FormData();
          formData.append("file", loadedFiles[0]);
          sendRequest<AssetInterface>(
            CoreAPI_URLs.ASSETS_UPLOAD,
            (uploadResponse) => {
              if (paymentId && !patientId) {
                const dataRelation = {
                  payment: { id: paymentId },
                  asset: { id: uploadResponse.id },
                  customCategory: defaultCategory ? openedCategory : null,
                };
                sendRequest(
                  CoreAPI_URLs.PAYMENT_ASSET_RELATION,
                  fileUpLoad,
                  "POST",
                  JSON.stringify(dataRelation)
                );
              } else if (patientId && !paymentId) {
                const dataRelation = {
                  patient: { id: patientId },
                  asset: { id: uploadResponse.id },
                  customCategory: defaultCategory ? openedCategory : null,
                };
                sendRequest(
                  CoreAPI_URLs.ASSET_PATIENT_RELATION,
                  fileUpLoad,
                  "POST",
                  JSON.stringify(dataRelation)
                );
              } else if (!patientId && !paymentId && providerId) {
                const dataRelation = {
                  provider: { id: providerId },
                  asset: { id: uploadResponse.id },
                  customCategory: defaultCategory ? openedCategory : null,
                };
                sendRequest(
                  CoreAPI_URLs.PROVIDER_ASSET_RELATION,
                  fileUpLoad,
                  "POST",
                  JSON.stringify(dataRelation)
                );
              } else {
                fileUpLoad();
              }
            },
            "POST",
            formData,
            [{ header: HeaderEnum.RESOURCE_ID, value: response.id }]
          );
        }
      },
      "POST",
      JSON.stringify(data)
    );
  };

  const fileUpLoad = () => {
    setIsDataUpdated(true);
  };

  return (
    <>
      <PermissionWrapper
        requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB]?.UPLOAD_DOCUMENTS]}
      >
        {
          <Button name="cy-upload-file" variant="contained" color="primary" onClick={() => setOpen(true)}>
            {t("documentViewer.uploadFile")}
          </Button>
        }
      </PermissionWrapper>
      <DropzoneDialog
        inputProps={{ className: "visually-hidden-input" }}
        acceptedFiles={[
          MediaMimeTypesEnum.IMAGE_BMP,
          MediaMimeTypesEnum.IMAGE_JPEG,
          MediaMimeTypesEnum.IMAGE_JPG,
          MediaMimeTypesEnum.DOCUMENT_PDF,
          MediaMimeTypesEnum.IMAGE_PJPEG,
          MediaMimeTypesEnum.IMAGE_PNG,
          MediaMimeTypesEnum.IMAGE_TIFF,
          MediaMimeTypesEnum.IMAGE_XPNG,
          MediaMimeTypesEnum.DOCUMENT_DOC,
          MediaMimeTypesEnum.DOCUMENT_DOCX,
        ]}
        cancelButtonText={t("documentViewer.cancel")}
        submitButtonText={t("documentViewer.submit")}
        dialogTitle={t("documentViewer.uploadFileTitle")}
        dropzoneText={t("documentViewer.dropzoneText")}
        getFileAddedMessage={(fileName: string) => t("documentViewer.fileAdded", { fileName })}
        getFileRemovedMessage={(fileName: string) => t("documentViewer.fileRemoved", { fileName })}
        getFileLimitExceedMessage={(filesLimit: number) => t("documentViewer.fileLimitExceed", { filesLimit })}
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleDialogChange}
        showPreviews={true}
        showFileNamesInPreview={true}
        filesLimit={1}
        maxFileSize={3000000}
      />
    </>
  );
};

export default Dropzone;
